import type { SocialReportProfile } from "contracts/api/social/report";
import {
  SOCIAL_FINDING_SEVERITIES,
  type SocialFindingSeverity,
} from "contracts/enums/social";
import { REPORT_SEVERITY_LABELS } from "../../enums/social-report";
import { pluralize } from "../../ui/format";
import {
  clientSummary,
  totalFindings,
  type VerifiedChecks,
} from "./socialReportModel";

/**
 * The whole-client rollup above the profile sections. Unverifiable and
 * not-applicable checks are stated in their own words, never folded into the
 * pass/fail line — the denominator only holds checks the evidence settled.
 */
export function SocialReportSummary(props: {
  profiles: SocialReportProfile[];
}) {
  const summary = () => clientSummary(props.profiles);
  return (
    <section class="social-report-summary" aria-label="Social report summary">
      <div class="social-report-summary-facts">
        <SummaryFact
          value={summary().profiles}
          label={pluralize(summary().profiles, "profile", "profiles")}
        />
        <SummaryFact
          value={summary().capturedPosts}
          label={pluralize(
            summary().capturedPosts,
            "post captured",
            "posts captured",
          )}
        />
        <SummaryFact
          value={summary().analysedPosts}
          label={pluralize(
            summary().analysedPosts,
            "post analysed",
            "posts analysed",
          )}
        />
        <SummaryFact
          value={totalFindings(summary().findings)}
          label={pluralize(
            totalFindings(summary().findings),
            "finding",
            "findings",
          )}
        />
      </div>
      <ChecksLine checks={summary().checks} />
      <FindingsBySeverity findings={summary().findings} />
    </section>
  );
}

function SummaryFact(props: { value: number; label: string }) {
  return (
    <div class="social-report-fact">
      <strong>{props.value}</strong>
      <span>{props.label}</span>
    </div>
  );
}

/** Shared wording for a verified-checks rollup at client or profile level. */
export function ChecksLine(props: { checks: VerifiedChecks }) {
  const checks = () => props.checks;
  return (
    <p class="social-report-checks">
      <strong>
        {checks().passed} of {checks().verified}{" "}
        {pluralize(checks().verified, "verified check", "verified checks")}{" "}
        passed.
      </strong>{" "}
      <Caveat
        count={checks().unableToVerify}
        wording="could not be settled by the captured evidence and are not counted as failures"
      />
      <Caveat
        count={checks().notApplicable}
        wording="did not apply and are excluded"
      />
    </p>
  );
}

function Caveat(props: { count: number; wording: string }) {
  if (props.count === 0) return null;
  return (
    <span class="social-report-caveat">
      {props.count} {pluralize(props.count, "check", "checks")} {props.wording}.{" "}
    </span>
  );
}

function FindingsBySeverity(props: {
  findings: Record<SocialFindingSeverity, number>;
}) {
  if (totalFindings(props.findings) === 0) return null;
  return (
    <div class="social-report-severities">
      {SOCIAL_FINDING_SEVERITIES.filter(
        (severity) => props.findings[severity] > 0,
      ).map((severity) => (
        <span class={`badge badge-sev sev-${severity}`}>
          {props.findings[severity]} {REPORT_SEVERITY_LABELS[severity]}
        </span>
      ))}
    </div>
  );
}
